import { Localized } from "@fluent/react";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import {
    Alert, Dialog, DialogActions, DialogContent, DialogTitle, Grid, TextField,
    RadioGroup, FormControlLabel, Radio, Typography, Box, Skeleton, Stack, Avatar,
    Autocomplete, Checkbox, FormGroup, Divider
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { alpha } from "@mui/material/styles";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import DevicesOtherOutlinedIcon from "@mui/icons-material/DevicesOtherOutlined";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import PersonIcon from "@mui/icons-material/Person";

import { AppId } from "../../../../app/appTypes";
import { SaveButton } from "../../../../app/components/SaveButton";
import { CancelButton } from "../../../../app/components/CancelButton";
import { RequiredError } from "../../../../app/components/RequiredError";
import { showError, showSuccess } from "../../../notifications/notificationsSlice";
import { selectStructureNodeById } from "../../../device/structureSlice";
import { DocumentLanguages, DocumentTypes } from "../../../documents/documentTypes";
import { DocumentLanguageSelect } from "../../../documents/controls/DocumentLanguageSelect";
import { ClientDocumentTemplateSelectField } from "../../../documents/templates/ClientDocumentTemplateSelectField";
import { LoadUsers } from "../../../../app/AppDataLoader";
import { selectAllUsers } from "../../../users/usersSlice";

import {
    CreationMode, DocumentToCreate, prepareValidationStage,
    PrepareValidationStagePayload, ProcessStageOverview
} from "../validationProcessApi";

type AksDocType = Extract<DocumentTypes, "ACS" | "ACM" | "ACF" | "ACP">;

type UserOption = {
    userName: string;
    firstname: string;
    surname: string;
    email: string;
    position?: string;
};

export interface AksStageActivationProps {
    stageCode: "ACS";
    processId: AppId | null;
    structureId: number;
    processName?: string;
    processNumber?: string;
    validatedObjectType?: string;
    validatedObjectName?: string;
    onSuccess: (stage: ProcessStageOverview) => void;
    onCancel: () => void;
}

type PerDocState = {
    number: string;
    numberDirty: boolean;
    title: string;
    titleDirty: boolean;
    creationMode: CreationMode;
    templateId: string | "";
};

const DEFAULT_DOCS: AksDocType[] = ["ACS", "ACM", "ACF", "ACP"];

const DOC_DESCRIPTIONS: Record<AksDocType, { pl: string; en: string }> = {
    ACS: { pl: "Analiza krytyczności systemu", en: "System Criticality Analysis" },
    ACM: { pl: "Analiza krytyczności modułów", en: "Modules Criticality Analysis" },
    ACF: { pl: "Analiza krytyczności funkcjonalności", en: "Functions Criticality Analysis" },
    ACP: { pl: "Analiza krytyczności procesów", en: "Processes Criticality Analysis" },
};

export const AksStageActivation: React.FC<AksStageActivationProps> = (props) => {
    const dispatch = useAppDispatch();
    const allUsers = useAppSelector(selectAllUsers);

    const users: UserOption[] = React.useMemo(
        () =>
            (allUsers ?? []).map((u: any) => ({
                userName: String(u.userName),
                firstname: u.firstName ?? u.firstname ?? "",
                surname: u.lastName ?? u.surname ?? "",
                email: u.email ?? "",
                position: u.position,
            })),
        [allUsers]
    );

    const findUserByUsername = (username: string | null, list: UserOption[]) =>
        username ? list.find(u => u.userName === username) ?? null : null;

    // Wspólne pola
    const [language, setLanguage] = useState<DocumentLanguages>("PL");
    const [responsible, setResponsible] = useState<string | null>(null);
    const [responsibleDirty, setResponsibleDirty] = useState(false);

    // Jakie dokumenty tworzymy
    const [selectedTypes, setSelectedTypes] = useState<AksDocType[]>([...DEFAULT_DOCS]);

    // Per-dokument stan
    const [docState, setDocState] = useState<Record<AksDocType, PerDocState>>({
        ACS: { number: "", numberDirty: false, title: "", titleDirty: false, creationMode: "TEMPLATE", templateId: "" },
        ACM: { number: "", numberDirty: false, title: "", titleDirty: false, creationMode: "TEMPLATE", templateId: "" },
        ACF: { number: "", numberDirty: false, title: "", titleDirty: false, creationMode: "TEMPLATE", templateId: "" },
        ACP: { number: "", numberDirty: false, title: "", titleDirty: false, creationMode: "TEMPLATE", templateId: "" },
    });

    const [saving, setSaving] = useState(false);
    const [serverError, setServerError] = useState("");

    const structure = useAppSelector((state) =>
        props.structureId ? selectStructureNodeById(state, props.structureId) : null
    );

    const structureDisplay = useMemo(() => {
        const n = structure?.name ?? "";
        const nEN = structure?.name_EN ?? "";
        const nm = language === "EN" ? (nEN || n) : (n || nEN);
        const code = structure?.code ? ` (${structure.code})` : "";
        return (nm || structure?.code) ? `${nm}${code}` : "-";
    }, [structure?.name, structure?.name_EN, structure?.code, language]);

    // Generatory numeru i tytułu per typ
    const buildDefaultNumber = (type: string) => {
        const procNo = props.processNumber?.trim();
        if (!procNo || !type) return "";
        const t = String(type).toUpperCase(); // DocumentCode
        return `${procNo}/${t}`;
    };

    const buildDefaultTitle = (
        type: AksDocType,
        langCode?: string,
        st?: { name?: string; name_EN?: string; code?: string }
    ) => {
        if (!type || !langCode) return "";
        const lc = langCode.toUpperCase();

        // Tekst nagłówka wg typu dokumentu (PL/EN)
        const byType = DOC_DESCRIPTIONS[type];
        const titlePart = lc === "EN" ? byType.en : byType.pl;

        const objName =
            lc === "EN" ? (st?.name_EN ?? st?.name ?? "") : (st?.name ?? st?.name_EN ?? "");
        const objPart = objName ? `"${objName}"${st?.code ? ` (${st.code})` : ""}` : "";

        // np. "ACS — Analiza krytyczności systemu dla "XYZ" (DEV-001)"
        const forPhrase = lc === "EN" ? "for" : "dla";
        return objPart ? `${type} — ${titlePart} ${forPhrase} ${objPart}` : `${type} — ${titlePart}`;
    };

    // Ustaw domyślne numery/tytuły przy starcie i przy zmianach języka/struktury
    useEffect(() => {
        setDocState(prev => {
            const copy = { ...prev };
            DEFAULT_DOCS.forEach(dt => {
                const cur = copy[dt];
                if (!cur.numberDirty) {
                    cur.number = buildDefaultNumber(dt);
                }
                if (!cur.titleDirty) {
                    cur.title = buildDefaultTitle(dt, String(language), structure ?? undefined);
                }
            });
            return { ...copy };
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [language, structure?.name, structure?.name_EN, structure?.code]);

    // Walidacja
    const isResponsibleOk = !!(responsible && responsible.trim() !== "");
    const isPerDocOk = selectedTypes.every(dt => {
        const s = docState[dt];
        const numberOk = !!s.number.trim();
        const titleOk = !!s.title.trim();
        const templateOk = s.creationMode === "EMPTY" ? true : (s.templateId !== "" && Number(s.templateId) > 0);
        return numberOk && titleOk && templateOk;
    });
    const isFormValid = !!props.processId && !!language && isResponsibleOk && isPerDocOk && selectedTypes.length > 0;

    // Handlery per-dokument
    const handleDocNumberChange = (dt: AksDocType, v: string) => {
        setDocState(prev => ({ ...prev, [dt]: { ...prev[dt], number: v, numberDirty: true } }));
    };
    const handleDocTitleChange = (dt: AksDocType, v: string) => {
        setDocState(prev => ({ ...prev, [dt]: { ...prev[dt], title: v, titleDirty: true } }));
    };
    const handleDocModeChange = (dt: AksDocType, mode: CreationMode) => {
        setDocState(prev => ({ ...prev, [dt]: { ...prev[dt], creationMode: mode } }));
    };
    const handleDocTemplateChange = (dt: AksDocType, id: string | "") => {
        setDocState(prev => ({ ...prev, [dt]: { ...prev[dt], templateId: id } }));
    };

    // Zaznaczanie ACS/ACM/ACF/ACP
    const toggleType = (dt: AksDocType) => {
        setSelectedTypes(prev =>
            prev.includes(dt) ? prev.filter(x => x !== dt) : [...prev, dt]
        );
    };

    const handleSaveClick = async () => {
        if (!isFormValid) {
            setServerError("fill-all-required-fields");
            return;
        }

        const documents: DocumentToCreate[] = selectedTypes.map((dt) => {
            const st = docState[dt];
            return {
                type: dt as unknown as DocumentTypes, // "ACS" | "ACM" | "ACF" | "ACP"
                number: st.number.trim(),
                title: st.title.trim(),
                language,
                responsible: responsible?.trim() || null,
                creationMode: st.creationMode,
                templateId: st.creationMode === "TEMPLATE" ? Number(st.templateId) : 0,
            };
        });

        const payload: PrepareValidationStagePayload = {
            stageCode: props.stageCode, // "AKS"
            processId: props.processId!,
            documents,
            toLink: []
        };

        setSaving(true);
        setServerError("");
        try {
            const result = await dispatch(prepareValidationStage(payload)).unwrap();
            dispatch(showSuccess("saved"));
            props.onSuccess(result as ProcessStageOverview);
        } catch {
            setServerError("error");
            dispatch(showError("error"));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open fullWidth maxWidth="md"
            slotProps={{
                backdrop: { sx: { bgcolor: (t) => alpha(t.palette.grey[900], 0.5) } },
            }}
        >
            <DialogTitle sx={{ fontWeight: 700 }}>
                <Localized id="aks-stage-activation-title">
                    <span>Przygotowanie dokumentów AKS (Analiza Krytyczności Systemu)</span>
                </Localized>
            </DialogTitle>

            <DialogContent sx={{ pt: 1 }}>
                {/* Info */}
                <Box sx={(t) => ({
                    display: "flex", alignItems: "flex-start", gap: 2, p: 2, mb: 2,
                    border: `1px solid ${t.palette.divider}`, borderRadius: 1.5, bgcolor: t.palette.action.hover,
                })}>
                    <InfoOutlinedIcon sx={{ mt: 1, flexShrink: 0 }} />
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                            Dokumenty zostaną przygotowane dla:
                        </Typography>

                        <Stack spacing={0.5}>
                            <Box sx={{ display: "grid", gridTemplateColumns: "auto 1fr", columnGap: 1 }}>
                                <Typography variant="body2" color="text.secondary" sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                                    <AssignmentOutlinedIcon fontSize="small" />
                                    <Box component="span" sx={{ fontWeight: 600 }}>Proces:</Box>
                                </Typography>
                                <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                                    {(props.processName || "-")}{props.processNumber ? ` (${props.processNumber})` : ""}
                                </Typography>
                            </Box>
                            <Box sx={{ display: "grid", gridTemplateColumns: "auto 1fr", columnGap: 1 }}>
                                <Typography variant="body2" color="text.secondary" sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                                    <ApartmentOutlinedIcon fontSize="small" />
                                    <Box component="span" sx={{ fontWeight: 600 }}>Struktura:</Box>
                                </Typography>
                                <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                                    {structureDisplay}
                                </Typography>
                            </Box>
                            {props.validatedObjectType?.trim() && (
                                <Box sx={{ display: "grid", gridTemplateColumns: "auto 1fr", columnGap: 1 }}>
                                    <Typography variant="body2" color="text.secondary" sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                                        <DevicesOtherOutlinedIcon fontSize="small" />
                                        <Box component="span" sx={{ fontWeight: 600 }}>Typ walidowanego obiektu:</Box>
                                    </Typography>
                                    <Typography variant="body2">{props.validatedObjectType}</Typography>
                                </Box>
                            )}
                            {props.validatedObjectName?.trim() && (
                                <Box sx={{ display: "grid", gridTemplateColumns: "auto 1fr", columnGap: 1 }}>
                                    <Typography variant="body2" color="text.secondary" sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                                        <AssignmentOutlinedIcon fontSize="small" />
                                        <Box component="span" sx={{ fontWeight: 600 }}>Nazwa obiektu:</Box>
                                    </Typography>
                                    <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                                        {props.validatedObjectName}
                                    </Typography>
                                </Box>
                            )}
                        </Stack>
                    </Box>

                </Box>

                {/* Wspólne: Język + Odpowiedzialny */}
                <Grid container spacing={2} sx={{ mt: 1, mb: 0.5 }}>
                    <Grid item xs={12} md={6}>
                        <DocumentLanguageSelect defaultValue={language} required onChange={(val: any) => setLanguage(val)} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <LoadUsers component={<Skeleton variant="rectangular" height={56} />}>
                            <Autocomplete<UserOption, false, false, false>
                                options={users}
                                onChange={(_, u) => { setResponsible(u?.userName ?? null); setResponsibleDirty(true); }}
                                onBlur={() => setResponsibleDirty(true)}
                                value={findUserByUsername(responsible, users)}
                                isOptionEqualToValue={(o, v) => o.userName === v.userName}
                                getOptionLabel={(u) => (u ? `${u.firstname} ${u.surname} (${u.email})` : "")}
                                renderOption={(props, u) => (
                                    <li {...props} key={u.userName}>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <Avatar sx={{ width: 28, height: 28, fontSize: 12, lineHeight: 1 }} src={(u as any).avatarUrl}>
                                                {<PersonIcon sx={{ fontSize: 16 }} />}
                                            </Avatar>
                                            <Box>
                                                <Typography variant="body2" fontWeight={600}>{u.firstname} {u.surname}</Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {u.email}{u.position ? ` · ${u.position}` : ""}
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    </li>
                                )}
                                renderInput={(params) => (
                                    <TextField {...params}
                                        label="Osoba odpowiedzialna"
                                        placeholder="Wybierz osobę"
                                        required
                                        error={responsibleDirty && !isResponsibleOk}
                                        helperText={responsibleDirty && !isResponsibleOk ? <RequiredError /> : null}
                                    />
                                )}
                            />
                        </LoadUsers>
                    </Grid>
                </Grid>

                {/* Wybór typów dokumentów (z opisami) */}
                <Box sx={{ mt: 1 }}>
                    <Box sx={(t) => ({
                        bgcolor: t.palette.action.hover,
                        px: 2,
                        py: 1,
                        borderRadius: 1,
                        mb: 1,
                    })}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            Jakie dokumenty utworzyć?
                        </Typography>
                    </Box>
                    <FormGroup>
                        {(DEFAULT_DOCS as AksDocType[]).map(dt => {
                            const desc = language === "EN" ? DOC_DESCRIPTIONS[dt].en : DOC_DESCRIPTIONS[dt].pl;
                            return (
                                <FormControlLabel
                                    key={dt}
                                    control={<Checkbox checked={selectedTypes.includes(dt)} onChange={() => toggleType(dt)} />}
                                    label={<Typography variant="body2">{desc}({dt})</Typography>}
                                />
                            );
                        })}
                    </FormGroup>

                </Box>

                <Divider sx={{ my: 1.5 }} />

                {/* Sekcje per dokument */}
                <Stack spacing={2}>
                    {selectedTypes.map((dt) => {
                        const s = docState[dt];
                        const desc = language === "EN" ? DOC_DESCRIPTIONS[dt].en : DOC_DESCRIPTIONS[dt].pl;
                        return (
                            <Box key={dt} sx={(t) => ({
                                border: `1px solid ${t.palette.divider}`, borderRadius: 1, p: 1.25,
                                bgcolor: t.palette.action.hover,
                            })}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                                    {desc} ({dt})
                                </Typography>

                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={12}>
                                        <TextField
                                            required fullWidth InputLabelProps={{ shrink: true }}
                                            value={s.number} error={!s.number.trim()}
                                            InputProps={{
                                                sx: {
                                                    backgroundColor: (t) => t.palette.background.paper,
                                                },
                                            }}
                                            onChange={(e) => handleDocNumberChange(dt, e.target.value)}
                                            label={<Localized id={`document-number-${dt}`}><span>_Numer dokumentu ({dt})</span></Localized>}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={12}>
                                        <TextField
                                            required fullWidth InputLabelProps={{ shrink: true }}
                                            InputProps={{
                                                sx: {
                                                    backgroundColor: (t) => t.palette.background.paper,
                                                },
                                            }}
                                            value={s.title} error={!s.title.trim()}
                                            onChange={(e) => handleDocTitleChange(dt, e.target.value)}
                                            label={<Localized id={`document-title-${dt}`}><span>_Tytuł ({dt})</span></Localized>}
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2" sx={{ mt: 0.5, mb: 0.5 }}>Tryb tworzenia</Typography>
                                        <RadioGroup
                                            row value={s.creationMode}
                                            onChange={(e) => handleDocModeChange(dt, e.target.value as CreationMode)}
                                        >
                                            <FormControlLabel value="EMPTY" control={<Radio size="small" />} sx={{ mr: 2 }} label={<Typography variant="body2">Pusty</Typography>} />
                                            <FormControlLabel value="TEMPLATE" control={<Radio size="small" />} label={<Typography variant="body2">Z szablonu</Typography>} />
                                        </RadioGroup>

                                        {s.creationMode === "TEMPLATE" && (
                                            <Box sx={{ mt: 1 }}>
                                                <ClientDocumentTemplateSelectField
                                                    structureId={props.structureId}
                                                    documentType={dt as unknown as DocumentTypes}
                                                    value={s.templateId}
                                                    onChange={(id) => handleDocTemplateChange(dt, id)}
                                                />
                                            </Box>
                                        )}
                                    </Grid>
                                </Grid>
                            </Box>
                        );
                    })}
                </Stack>

                {serverError && (
                    <Alert sx={{ mt: 2 }} onClose={() => setServerError("")} severity="error">
                        <Localized id={serverError}><span>Server error</span></Localized>
                    </Alert>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <SaveButton loading={saving} onClick={handleSaveClick} />
                <CancelButton loading={saving} onClick={props.onCancel} />
            </DialogActions>
        </Dialog>
    );
};
